import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, CheckCircle, XCircle } from "lucide-react";
import { ROMANIAN_COUNTIES } from "@/lib/romanianCounties";
import { validateCuiCnp } from "@/lib/cuiValidation";
import type { BuyerInfo } from "./types";

interface BuyerFormProps {
  buyer: BuyerInfo;
  buyerCity: string;
  buyerCounty: string;
  buyerVatRegistered: boolean | null;
  buyerEFactura: boolean | null;
  cuiLoading: boolean;
  onBuyerChange: (buyer: BuyerInfo) => void;
  onBuyerCityChange: (v: string) => void;
  onBuyerCountyChange: (v: string) => void;
  onLookupCUI: () => void;
}

const BuyerForm = ({
  buyer, buyerCity, buyerCounty, buyerVatRegistered, buyerEFactura,
  cuiLoading, onBuyerChange, onBuyerCityChange, onBuyerCountyChange, onLookupCUI,
}: BuyerFormProps) => {
  const cuiValidation = validateCuiCnp(buyer.cui);

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold">Client</h3>
        {buyerVatRegistered !== null && (
          <div className="flex gap-2">
            <Badge variant={buyerVatRegistered ? "default" : "secondary"} className="gap-1 text-xs">
              {buyerVatRegistered ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
              {buyerVatRegistered ? "Plătitor TVA" : "Neplătitor TVA"}
            </Badge>
            {buyerEFactura !== null && (
              <Badge variant={buyerEFactura ? "default" : "outline"} className="gap-1 text-xs">
                {buyerEFactura ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                {buyerEFactura ? "e-Factura activ" : "e-Factura inactiv"}
              </Badge>
            )}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label>CUI / CIF / CNP Client</Label>
        <div className="flex gap-2">
          <Input
            placeholder="ex: 12345678, RO12345678, CIF sau CNP"
            value={buyer.cui}
            onChange={(e) => onBuyerChange({ ...buyer, cui: e.target.value })}
            onBlur={onLookupCUI}
            className={!cuiValidation.valid ? "border-destructive focus-visible:ring-destructive" : ""}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={onLookupCUI} disabled={cuiLoading}>
                {cuiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Caută datele clientului după CUI</TooltipContent>
          </Tooltip>
        </div>
        {!cuiValidation.valid && cuiValidation.error && (
          <p className="text-xs text-destructive">{cuiValidation.error}</p>
        )}
        {cuiValidation.valid && cuiValidation.type === "cnp" && buyer.cui.trim() && (
          <p className="text-xs text-muted-foreground">Detectat: CNP (persoană fizică)</p>
        )}
        {cuiValidation.valid && cuiValidation.type === "cui" && buyer.cui.trim() && (
          <p className="text-xs text-muted-foreground">Detectat: CUI (firmă)</p>
        )}
      </div>
      <div className="space-y-2">
        <Label>Denumire</Label>
        <Input value={buyer.name} onChange={(e) => onBuyerChange({ ...buyer, name: e.target.value })} placeholder="Se completează automat" />
      </div>
      <div className="space-y-2">
        <Label>Adresă (stradă)</Label>
        <Input value={buyer.address} onChange={(e) => onBuyerChange({ ...buyer, address: e.target.value })} placeholder="Se completează automat" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Oraș</Label>
          <Input value={buyerCity} onChange={(e) => onBuyerCityChange(e.target.value)} placeholder="ex: Cluj-Napoca" />
        </div>
        <div className="space-y-2">
          <Label>Județ</Label>
          <Select value={buyerCounty} onValueChange={onBuyerCountyChange}>
            <SelectTrigger>
              <SelectValue placeholder="Selectează județul" />
            </SelectTrigger>
            <SelectContent>
              {ROMANIAN_COUNTIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default BuyerForm;
